/**
 * The steps of a pick day, as the stappenlijst on Bestellingen, Pickrondes and Tekorten shows
 * them: release the day, compose rounds, print their packing slips, pick them, handle the
 * shortages. The work is spread over three screens; this says per step how far the day is, and
 * which step is next, so a CMS user is led through it instead of having to know the way.
 *
 * Pure, from what those screens already read: the day's lock, its orders (summarisePickDay), its
 * rounds and the open shortages.
 */

import { WAVE_STATUS } from "../constants/PickingConstant";

export const PICK_DAY_STEPS = ["release", "compose", "print", "pick", "shortages"];

/** antd Steps statuses. */
const FINISH = "finish";
const PROCESS = "process";
const WAIT = "wait";

/**
 * @param {Object} day
 * @param {boolean} day.locked - the pick day is released
 * @param {number} day.total - its orders (summarisePickDay)
 * @param {number} day.unassigned - its orders that are in no round yet
 * @param {Array} day.waves - its rounds, cancelled ones included
 * @param {number} day.openShortages - orders with a shortage nobody has called about yet
 * @returns {{ steps: Array<{ key, status }>, current: string|null, finished: boolean, empty: boolean }}
 *   `current` is the next step to take; `finished` when every round is picked and no customer is
 *   left to call; `empty` when the day has no orders and no rounds.
 */
export const pickDaySteps = ({ locked = false, total = 0, unassigned = 0, waves = [], openShortages = 0 } = {}) => {
  const rounds = (waves || []).filter((wave) => wave.status !== WAVE_STATUS.CANCELLED);
  const printed = rounds.filter((wave) => !!wave.printedAt).length;
  const picked = rounds.filter((wave) => wave.status === WAVE_STATUS.DONE).length;

  const status = {
    release: locked ? FINISH : total > 0 ? PROCESS : WAIT,
    compose: !locked ? WAIT : unassigned > 0 ? PROCESS : rounds.length > 0 ? FINISH : WAIT,
    print: rounds.length === 0 ? WAIT : printed < rounds.length ? PROCESS : FINISH,
    pick: rounds.length === 0 ? WAIT : picked < rounds.length ? PROCESS : FINISH,
  };
  // Shortages can be called about while other rounds are still being picked
  status.shortages = openShortages > 0 ? PROCESS : status.pick === FINISH ? FINISH : WAIT;

  const counts = {
    release: { total },
    compose: { unassigned, rounds: rounds.length },
    print: { printed, rounds: rounds.length },
    pick: { picked, rounds: rounds.length },
    shortages: { open: openShortages },
  };

  const steps = PICK_DAY_STEPS.map((key) => ({ key, status: status[key], ...counts[key] }));
  // The next step is the first one not done, unless the day has nothing to pick at all
  const current = total === 0 && !locked ? null : PICK_DAY_STEPS.find((key) => status[key] !== FINISH) || null;
  return { steps, current, finished: current === null && rounds.length > 0, empty: total === 0 && rounds.length === 0 };
};
